import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';

import {
  BASE_AXIOS_CONFIG,
  handleError,
  isAuthRoute,
  toHandledError,
  unwrapResponse,
} from './http-config';
import { getErrorCode, isHttpStatus } from './error-utils';
import {
  isPreviewToken,
  PREVIEW_TOKEN_HEADER,
  PREVIEW_TOKEN_KEY,
} from './constants';
import { getQueryClient } from './query-client';
// Import cycle by design and benign: state/user reaches back here through
// services/user, but neither side touches the other's bindings at module load.
// The key must have one owner — a literal here that drifts from the one useUser
// reads leaves the app signed in against a session that is gone.
import { USER_QUERY_KEY } from '@/state/user';

const axiosInstance = axios.create({
  ...BASE_AXIOS_CONFIG,
  withCredentials: true, // Needed for cookies
});

// Unlocks read-only access to one unpublished studio for 30 minutes.
// sessionStorage, not localStorage: the token dies with the tab.
axiosInstance.interceptors.request.use((config) => {
  try {
    if (typeof window === 'undefined') return config;
    // Same shape check the middleware applies: the inline script in the root
    // layout stores whatever the query string carried, and a value with a
    // control character in it throws when the request is actually sent —
    // outside this try, so every call on the page would fail.
    const previewToken = sessionStorage.getItem(PREVIEW_TOKEN_KEY);
    if (isPreviewToken(previewToken)) {
      config.headers.set(PREVIEW_TOKEN_HEADER, previewToken);
    }
  } catch {}
  return config;
});

// Single-flight refresh state
let refreshPromise: Promise<any> | null = null;
let isRedirecting = false;
const AUTH_BAILOUT_KEY = '__AUTH_BAILOUT__';

const hasAuthBailout = () => {
  try {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(AUTH_BAILOUT_KEY) === '1';
  } catch {
    return false;
  }
};

const setAuthBailout = () => {
  try {
    if (typeof window !== 'undefined')
      sessionStorage.setItem(AUTH_BAILOUT_KEY, '1');
  } catch {}
};

const clearAuthBailout = () => {
  try {
    if (typeof window !== 'undefined')
      sessionStorage.removeItem(AUTH_BAILOUT_KEY);
  } catch {}
};

const isMeEndpoint = (url?: string) => {
  if (!url) return false;
  try {
    const parsed = url.startsWith('http') ? new URL(url) : null;
    const path = parsed ? parsed.pathname : url;
    return path === '/public/users/me';
  } catch {
    return url.endsWith('/public/users/me') || url.includes('/public/users/me');
  }
};

declare module 'axios' {
  interface AxiosRequestConfig {
    // Set by a request the backend refuses with LOGIN_REQUIRED before it writes
    // anything, so it is safe to send again once the session is refreshed.
    retryOnLoginRequired?: boolean;
  }
}

const isDashboardPage = () => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/dashboard');
};

const shouldRedirectOnAuthFailure = () => {
  // Only redirect from dashboard page on auth failure
  // Users should remain on other pages (checkout, classes, packages, etc.)
  return isDashboardPage();
};

// The session lives in HttpOnly cookies, so the browser cannot end it — only
// the server logout can. The one piece of session state this side owns is the
// cached user, and an errored query keeps its last data, which would leave
// consumers signed in against a session that is gone. Dropping it mirrors what
// the logout button does; clearing cookies or localStorage here would only cost
// the visitor their cart and their analytics identity while the session cookies
// it was aimed at stayed untouched.
const onSessionEnded = (shouldRedirect: boolean) => {
  getQueryClient().setQueryData(USER_QUERY_KEY, null);

  if (!shouldRedirect || isRedirecting || typeof window === 'undefined') return;
  isRedirecting = true;

  window.location.replace(
    `/?action=login&redirect=${encodeURIComponent(
      window.location.pathname + window.location.search,
    )}`,
  );
};

const rejectAuthError = (error: any) =>
  Promise.reject(toHandledError(error, 'Not authorized'));

// Only the server refusing the refresh ends the session. A 429, a 5xx or a
// request that never got an answer (a phone waking from sleep, an app resumed
// before its network is back) must leave the next 401 free to try again.
const isRefusedRefresh = (error: unknown) =>
  isHttpStatus(error, 401) || isHttpStatus(error, 403);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // A successful sign-in, sign-up or refresh replaces whatever session state
    // caused an earlier bailout, so the flag must not outlive it — nothing else
    // ever clears it, and while it is set no 401 is ever retried.
    if (isAuthRoute(response.config?.url)) {
      clearAuthBailout();
    }
    return unwrapResponse(response);
  },
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = (error?.config || {}) as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // 401 handling: /me and every other /public GET share one single-flight
    // refresh and retry once (a refused refresh logs out, whichever request
    // asked), so an expired access cookie on an open tab recovers
    // silently instead of surfacing an error (the auth-gated data endpoints —
    // codes lists, booked classes — depend on this). POSTs are excluded:
    // replaying a mutation after refresh is not safe, except a LOGIN_REQUIRED
    // refusal of one that opts in with retryOnLoginRequired. Auth endpoints are
    // excluded: their 401s mean wrong credentials, not an expired session.
    const isRefreshableGet =
      (originalRequest.method ?? 'get').toLowerCase() === 'get' &&
      !isAuthRoute(originalRequest.url);

    const shouldAttemptRefresh =
      status === 401 &&
      !originalRequest._retry &&
      (isMeEndpoint(originalRequest.url) ||
        isRefreshableGet ||
        (!!originalRequest.retryOnLoginRequired &&
          error?.response?.data?.code === 'LOGIN_REQUIRED')) &&
      !hasAuthBailout();

    if (shouldAttemptRefresh) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = axiosInstance
          .post('/public/auth/refresh', {})
          .catch((refreshErr) => {
            if (isRefusedRefresh(refreshErr)) {
              setAuthBailout();
            }
            throw refreshErr;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      try {
        await refreshPromise;
      } catch (refreshFailure) {
        // A refused refresh means the session is gone, whichever request set
        // it off. Left to /me, a read that meets the dead session first sets
        // the latch, under which a /me 401 clears nothing, so the page would
        // stay signed in until a reload. A guest has no user cached to clear.
        // A refresh that was not refused leaves the query its data, so the
        // next focus tries again.
        if (isRefusedRefresh(refreshFailure)) {
          onSessionEnded(shouldRedirectOnAuthFailure());
        }
        // The refresh rejection belongs to /public/auth/refresh, not to this
        // request: surfacing it would rename every failure AuthError and lose
        // the code callers branch on (LOGIN_REQUIRED and friends).
        return rejectAuthError(error);
      }

      return await axiosInstance.request(originalRequest);
    }

    if (
      status === 401 &&
      originalRequest._retry &&
      isMeEndpoint(originalRequest.url)
    ) {
      setAuthBailout();
      onSessionEnded(shouldRedirectOnAuthFailure());
    }

    if (status === 401 || status === 403) {
      return rejectAuthError(error);
    }

    return handleError(error);
  }
);

export const get = <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  axiosInstance.get(url, config);
export const post = async <TResponse, TRequest = any>(
  url: string,
  data?: TRequest,
  config?: AxiosRequestConfig
): Promise<any> => {
  const response = await axiosInstance.post<TResponse>(url, data, config);
  return response;
};
export const put = axiosInstance.put;
export const patch = axiosInstance.patch;
export const deleteRequest = axiosInstance.delete;

export { handleError };

export const isHttpNotFound = (error: unknown): boolean =>
  isHttpStatus(error, 404);

export const isHttpForbidden = (error: unknown): boolean =>
  isHttpStatus(error, 403);

// Backend code for routes a guest can reach but not read without a session —
// the caller offers a sign-in prompt instead of a generic failure.
export const isLoginRequired = (error: unknown): boolean =>
  getErrorCode(error) === 'LOGIN_REQUIRED';

